import { AfterViewInit, Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedTooth, Tooth, ToothCross } from '@features/client/order-workflow/models/tooth.model';
import { ToothCrossService } from '@features/client/order-workflow/services/tooth-cross.service';
import { ToothPart } from '@features/client/order-workflow/enums/tooth-part.enum';
import { RotaryDialButton } from '@features/client/order-workflow/models/rotary-dial-button.model';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import { isNil } from 'lodash';
import { FieldType } from '@ngx-formly/core';
import { ProductConfigurationFacade } from '@features/client/order-workflow/facades/product-configuration.facade';
import { Store } from '@ngxs/store';
import { OrderWorkflowSelectors } from '@features/client/order-workflow/state/order-workflow.selectors';
import { DentalValidatorService } from '@core/services/dental-validator.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormFacade } from '@core/services/form.facade';
import { filter, finalize, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { jawTeeth } from '@features/client/order-workflow/configs/jaw-teeth.config';
import { Order } from '@features/client/order-workflow/models/order.model';
import { OrderWorkflowFacade } from '@features/client/order-workflow/facades/order-workflow.facade';
import { FormBuilderContainer } from '@features/client/form-builder/containers/form-builder/form-builder.container';
import { PatchOrder } from '@features/client/order-workflow/state/order-workflow.actions';
import { OrderWorkflowStep } from '@features/client/order-workflow/enums/order-workflow-step.enum';
import { AccordionService } from '@shared/modules/accordion/services/accordion.service';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { CapturumButtonComponent } from '@capturum/ui/button';
import { JawStateDisabledPipe } from '@features/client/order-workflow/pipes/jaw-state-disabled.pipe';
import { ToothComponent } from '@features/client/order-workflow/components/tooth/tooth.component';
import { InArrayPipe } from '@features/client/order-workflow/pipes/in-array.pipe';
import { RotaryDialComponent } from '@features/client/order-workflow/components/rotary-dial/rotary-dial.component';
import { CrownActionsComponent } from '@features/client/order-workflow/components/crown-actions/crown-actions.component';
import { ToothConnectionComponent } from '@features/client/order-workflow/components/tooth-connection/tooth-connection.component';
import { ProgressSpinner } from 'primeng/progressspinner';
import { CapturumValidatorComponent } from '@capturum/ui/api';
import { TranslateDentalPipe } from '@shared/pipes/translate-dental.pipe';

enum JawSelection {
  upperJaw = 'upper_jaw',
  lowerJaw = 'lower_jaw',
  fullMouth = 'full_mouth',
}

@Component({
  selector: 'app-tooth-cross',
  templateUrl: './tooth-cross.container.html',
  styleUrls: ['./tooth-cross.container.scss'],
  imports: [
    TooltipComponent,
    TranslatePipe,
    AsyncPipe,
    CapturumButtonComponent,
    JawStateDisabledPipe,
    NgClass,
    ToothComponent,
    InArrayPipe,
    RotaryDialComponent,
    CrownActionsComponent,
    ToothConnectionComponent,
    ProgressSpinner,
    CapturumValidatorComponent,
    FormBuilderContainer,
    TranslateDentalPipe,
  ],
})
export class ToothCrossContainer extends FieldType implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('mountCheckbox', { static: true }) private mountArticulatorForm: FormBuilderContainer;
  @ViewChild('toothCrossFooter', { static: true }) private toothCrossFooterForm: FormBuilderContainer;

  public readonly formBuilder = inject(FormBuilder);
  public toothCross$: Observable<ToothCross>;
  public rotaryDialButtons$: Observable<RotaryDialButton[]>;
  public selectedTooth: SelectedTooth;
  public currentRotaryDialTooth: number = -1;
  public hasCrownActions$: Observable<boolean>;
  public readonly: boolean;
  public showRoot$: Observable<boolean>;
  public toothPart: typeof ToothPart = ToothPart;
  public isSummaryPage: boolean;
  public isLoading$: Observable<boolean>;
  public order$: BehaviorSubject<Order> = new BehaviorSubject<Order>(null);
  public toothHoverConfig: { numbers: number[] } = { numbers: [] };
  public toothCrossForm: FormGroup = this.formBuilder.group({
    toothCross: [null],
  });

  public teethInJaw: { upper_jaw: number[]; lower_jaw: number[]; full_mouth: number[] };
  public jawSelection = JawSelection;
  public selectedTeeth$: Observable<number[]>;

  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private toothCrossZoneFullMouth = 'tooth_cross_zone_fm';

  constructor(
    public readonly productConfigurationFacade: ProductConfigurationFacade,
    public readonly toothCrossService: ToothCrossService,
    private readonly validatorService: DentalValidatorService,
    private readonly formFacade: FormFacade,
    private readonly orderWorkflowFacade: OrderWorkflowFacade,
    private readonly accordionService: AccordionService,
  ) {
    super();

    this.teethInJaw = {
      upper_jaw: jawTeeth.upperJaw,
      lower_jaw: jawTeeth.lowerJaw,
      full_mouth: jawTeeth.fullMouth,
    };
  }

  public ngOnInit(): void {
    this.validatorService.reset();

    this.isLoading$ = this.productConfigurationFacade.getIsLoading();
    this.rotaryDialButtons$ = this.toothCrossService.getRootTypes();
    this.hasCrownActions$ = this.toothCrossService.hasCrownActions();
    this.showRoot$ = this.toothCrossService.getShowRoot();
    this.selectedTeeth$ = this.toothCrossService.getSelectedTeeth();

    this.watchOrderProductChanges();
    this.fetchProductConfiguration();
    this.setToothCrossForms();

    this.toothCross$ = this.toothCrossService.getToothCross().pipe(
      filter((value) => {
        return !isNil(value);
      }),
      finalize(() => {
        return (this.selectedTooth = null);
      }),
      map((toothCross: ToothCross) => {
        if (!toothCross.is_editable) {
          this.toothCrossService.setReadonly(true);
        }

        const value = {
          groups: toothCross.groups.map((group) => {
            return {
              teeth: group.teeth,
            };
          }),
        };

        return {
          groups: [
            { teeth: value.groups[0].teeth.concat(value.groups[1].teeth) },
            { teeth: value.groups[2].teeth.concat(value.groups[3].teeth) },
          ],
        };
      }),
      shareReplay(1),
    );

    this.toothCrossService
      .getReadonly()
      .pipe(
        filter((readonly) => {
          return !isNil(readonly);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        return (this.readonly = value);
      });

    this.accordionService
      .getActiveTab()
      .pipe(first())
      .subscribe((step) => {
        this.isSummaryPage = step === OrderWorkflowStep.summary;

        if (!this.isSummaryPage) {
          this.setBackEndValidation();
        }
      });
  }

  public ngAfterViewInit(): void {
    merge(this.mountArticulatorForm.form.valueChanges, this.toothCrossFooterForm.form.valueChanges)
      .pipe(
        map(() => {
          return { ...this.mountArticulatorForm.form.value, ...this.toothCrossFooterForm.form.value };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.store.dispatch(new PatchOrder(data));
      });
  }

  public ngOnDestroy(): void {
    this.toothCrossService.ngOnDestroy();
    this.toothCrossService.setMountArticulationForm(null);
    this.toothCrossService.setToothCrossFooterForm(null);
  }

  public handleDefaultClick(tooth: Tooth, toothPart: ToothPart): void {
    if (this.toothHoverConfig.numbers.includes(tooth.number) || tooth.crown.orderProducts.length) {
      const selectedTooth = { tooth, toothPart };

      if (this.toothCrossService.isConfiguring() && toothPart !== ToothPart.crown) {
        this.productConfigurationFacade.setApplyConfiguration(true);

        this.productConfigurationFacade.onApplyConfigurationSet$
          .pipe(
            filter((isSet) => {
              return isSet === false;
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() => {
            return setTimeout(() => {
              return this.setSelectedTooth(selectedTooth);
            });
          });
      } else {
        this.setSelectedTooth(selectedTooth);
        this.toothCrossService.setSelectedTeeth([selectedTooth.tooth.number]);
      }
    }
  }

  public onToothSelection(tooth: Tooth, toothPart: ToothPart): void {
    const productZones =
      this.store.selectSnapshot(OrderWorkflowSelectors.getProductType)?.product?.toothCrossZones || [];

    if (productZones?.length) {
      this.toothCrossService.disableBatchSelectMode();
    } else {
      this.toothCrossService.enableBatchSelectMode();
    }

    if (this.toothCrossService.isBatchSelectionEnabled() && this.toothCrossService.isConfiguring()) {
      this.toothCrossService.toggleToothToSelectedTeeth(tooth.number);
    } else {
      this.selectedTooth = { tooth, toothPart };

      this.handleDefaultClick(tooth, toothPart);
    }
  }

  public closeDial(): void {
    this.currentRotaryDialTooth = -1;

    this.toothCrossService.clearSelectedTooth();
  }

  public closeCrownActions(): void {
    setTimeout(() => {
      this.toothCrossService.clearSelectedTooth();
      this.toothCrossService.resetCrownAction();
    });
  }

  public handleSelectJaw(jaw: JawSelection): void {
    const productType = this.store.selectSnapshot(OrderWorkflowSelectors.getProductType);
    const isUpperJawSelected = this.toothCrossService.isUpperJawSelectedSnapshot();

    let selectedTeeth = [];

    if (
      productType.product.toothCrossZones.find((zone) => {
        return zone.value === this.toothCrossZoneFullMouth;
      })
    ) {
      selectedTeeth = this.teethInJaw.full_mouth;
      this.toothCrossService.setIsUpperJawSelected(!isUpperJawSelected);
    } else {
      if (jaw === JawSelection.upperJaw) {
        selectedTeeth = this.teethInJaw.upper_jaw;

        this.toothCrossService.setIsUpperJawSelected(!isUpperJawSelected);
      } else if (jaw === JawSelection.lowerJaw) {
        const isLowerJawSelected = this.toothCrossService.isLowerJawSelectedSnapshot();

        selectedTeeth = this.teethInJaw.lower_jaw;

        this.toothCrossService.setIsLowerJawSelected(!isLowerJawSelected);
      }
    }

    this.toothCrossService.setSelectedTeeth(selectedTeeth);
  }

  public setTeethZoneClasses(toothNumber: number): void {
    if (this.store.selectSnapshot(OrderWorkflowSelectors.getProductType)) {
      const toothZones = this.store.selectSnapshot(OrderWorkflowSelectors.getProductType).product.toothCrossZones;
      const selectedToothZone = toothZones.find((zone) => {
        return zone.teeth.includes(toothNumber);
      });

      if (selectedToothZone) {
        this.toothHoverConfig.numbers = selectedToothZone.teeth;
      } else if (toothZones?.length) {
        this.toothHoverConfig = { numbers: [] };
      } else {
        this.toothHoverConfig = { numbers: [toothNumber] };
      }
    }
  }

  public setHoverStateJaw(location: string, isHovering: boolean): void {
    const selectedProductType = this.store.selectSnapshot(OrderWorkflowSelectors.getProductType);

    if (isHovering && selectedProductType) {
      const availableZones = selectedProductType.product.toothCrossZones.filter((toothZone) => {
        return toothZone.teeth.some((toothNumber) => {
          return toothZone.value === 'tooth_cross_zone_fm'
            ? this.teethInJaw.full_mouth.includes(toothNumber)
            : this.teethInJaw[location].includes(toothNumber);
        });
      });

      if (availableZones.length) {
        const teethInZones = availableZones.reduce((teeth, toothCrossZone) => {
          return teeth.concat(toothCrossZone.teeth);
        }, []);

        this.toothHoverConfig = {
          numbers: teethInZones.filter((toothNumber) => {
            return teethInZones.includes(toothNumber);
          }),
        };
      } else {
        if (!selectedProductType.product.toothCrossZones.length) {
          this.toothHoverConfig = { numbers: this.teethInJaw[location] };
        } else {
          this.toothHoverConfig = { numbers: [] };
        }
      }
    } else {
      this.toothHoverConfig = { numbers: [] };
    }
  }

  private setSelectedTooth(selectedTooth: SelectedTooth): void {
    this.toothCrossService.setSelectedTeeth([selectedTooth.tooth.number]);
    this.toothCrossService.setSelectedTooth(selectedTooth);
    this.toothCrossService.setClickTooth(selectedTooth);

    if (selectedTooth.toothPart === ToothPart.root && !selectedTooth.tooth.root?.orderProduct?.id) {
      this.currentRotaryDialTooth = selectedTooth.tooth.number;
    }
  }

  /**
   * WatchOrderProductChange
   *
   * @description Whenever the orderProduct changes the selectedTeeth have to updated.
   * If the order product is empty then all teeth should be unselected.
   * If the order product belongs to a zone then all teeth of that zone should be selected
   * When the upper or lower jaw is selected then it should not alter the selected teeth.
   *
   * @private
   */
  private watchOrderProductChanges(): void {
    this.toothCrossService
      .getToothCross()
      .pipe(
        switchMap(() => {
          return combineLatest([
            this.store.select(OrderWorkflowSelectors.getOrderProduct),
            this.toothCrossService.getSelectedTooth(),
            this.toothCrossService.isLowerJawSelected(),
            this.toothCrossService.isUpperJawSelected(),
          ]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([orderProduct, selectedTooth, upperJawSelected, lowerJawSelected]) => {
        if (orderProduct && orderProduct.zone && !upperJawSelected && !lowerJawSelected) {
          this.toothCrossService.setSelectedTeeth(orderProduct.zone.teeth);
        } else if (!orderProduct && !selectedTooth) {
          this.toothCrossService.setSelectedTeeth([]);
        }
      });
  }

  private fetchProductConfiguration(): void {
    this.orderWorkflowFacade.toggleLoader(true);

    this.productConfigurationFacade.fetchProductConfiguration().subscribe((order) => {
      this.orderWorkflowFacade.toggleLoader(false);
      this.changeRemarkHeight();
      this.order$.next(order);
    });
  }

  private changeRemarkHeight(): void {
    this.toothCrossFooterForm.form?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((form) => {
      setTimeout(() => {
        const textarea = document.getElementById('remarks')?.getElementsByTagName('textarea')[0];
        const valueToExpandTextarea = 80;
        const isTextareaExpandable = form?.tooth_cross_files?.length || form?.remarks?.length > valueToExpandTextarea;

        if (textarea) {
          textarea.rows = isTextareaExpandable ? 4 : 2;
        }
      }, 0);
    });
  }

  private setToothCrossForms(): void {
    this.mountArticulatorForm.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.toothCrossService.setMountArticulationForm(this.mountArticulatorForm.form);
    });

    this.toothCrossFooterForm.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.toothCrossService.setToothCrossFooterForm(this.toothCrossFooterForm.form);
    });
  }

  private setBackEndValidation(): void {
    const order: Order = this.orderWorkflowFacade.getOrderSnapshot();

    if (order && !order.is_remake) {
      this.setBackEndErrors(this.toothCrossForm);
    } else if (order?.is_remake) {
      this.setBackEndErrors(this.toothCrossFooterForm?.form);
    }
  }

  private setBackEndErrors(form: FormGroup): void {
    if (form) {
      this.validatorService.backendValidation$
        .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
        .subscribe((errors: { [key: string]: string[] }) => {
          this.validatorService.setBackendErrorsOnForm(form, errors);
          this.formFacade.markAsTouched(form);
        });
    }
  }
}
